import { Component, ElementRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { DataService } from '../../../core/service/base/data.service';
import { FormGroup, FormBuilder, UntypedFormGroup } from '@angular/forms';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { LocationAccessGroup, LocationAccessGroupLevel } from '../../model/location-access-group';
import { LocationAccessGroupService } from '../../service/data/location-access-group.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { UserService } from '../../service/data/user.service';
import { AutoCompleteModel } from '../../../core/component/autocomplete/autocomplete-model';
import { AutocompleteComponent } from '../../../core/component/autocomplete/autocomplete.component';

@Component({
    selector: 'app-location-access-group-form',
    templateUrl: './location-access-group-form.component.html',
    styleUrls: ['./location-access-group-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: LocationAccessGroupService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LocationAccessGroupFormComponent extends FormComponentBase<LocationAccessGroup, LocationAccessGroupService> {
  constructor(
    private _fb: FormBuilder,
    private _fcf: FormControlExFactory,
    activatedRoute: ActivatedRoute,
    dataService: LocationAccessGroupService,
    dispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    private _webUserService: UserService
  ) {
    super(activatedRoute, dataService, messageService, navigation, dispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  protected createModel(): LocationAccessGroup {
    return new LocationAccessGroup();
  }

  get accessSelectionHeader(): string {
    return this.dataContext?.configurationLevel == LocationAccessGroupLevel.company
      ? 'LOCATION_ACCESS.COMPANIES_ACCESS'
      : 'LOCATION_ACCESS.PROPERTIES_ACCESS';
  }

  levelSelection: SelectionItem[] = [
    new SelectionItem('COMMON.COMPANY', LocationAccessGroupLevel.company),
    new SelectionItem('COMMON.PROPERTY', LocationAccessGroupLevel.property),
  ];
  accessSelection: AutoCompleteModel[] = [];
  memberSelection: AutoCompleteModel[] = [];

  targetSelectionControl: FormControlEx;
  memberSelectionControl: FormControlEx;

  selectedTargets: AutoCompleteModel[] = [];
  selectedMembers: AutoCompleteModel[] = [];

  protected init(data: LocationAccessGroup) {
    if (!data.isNew) {
      this.targetSelectionControl = this._fcf.create(
        data.configurationLevel == LocationAccessGroupLevel.company ? 'COMMON.COMPANY' : 'COMMON.PROPERTY',
        null
      );
      this.memberSelectionControl = this._fcf.create('USER.EDIT_HEADER', null);

      this.selectedTargets = data.accessRows.map(r => new AutoCompleteModel(r.caption, r.accessEntityId));
      this.selectedMembers = data.members.map(r => new AutoCompleteModel(`${r.personName} (${r.userName})`, r.webUserId));

      this.AddSubscription(
        this._webUserService
          .getList()
          .subscribe(
            res =>
              (this.memberSelection = res
                .filter(u => !this.selectedMembers.find(s => s.id == u.id))
                .map(r => new AutoCompleteModel(`${r.personName} (${r.name})`, r.id)))
          )
      );

      if (data.configurationLevel == LocationAccessGroupLevel.company) {
        this.AddSubscription(
          this.dataService.getAllCompanies().subscribe(res => {
            this.accessSelection = res
              .filter(c => !this.selectedTargets.find(t => t.id == c.id))
              .map(c => new AutoCompleteModel(c.toString(), c.id));
          })
        );
      } else {
        this.AddSubscription(
          this.dataService.getAllProperties().subscribe(res => {
            this.accessSelection = res
              .filter(c => !this.selectedTargets.find(t => t.id == c.id))
              .map(p => new AutoCompleteModel(p.toString(), p.id));
          })
        );
      }
    }
    super.init(data);
  }

  removeMember(index: number) {
    if (this.selectedMembers.length <= index) return;

    let model = this.selectedMembers[index];
    this.selectedMembers.splice(index, 1);
    this.memberSelection.push(model);
    this.form.markAsDirty();
  }

  addMember() {
    let id = this.memberSelectionControl.value?.id;
    if (!id) return;
    let model = this.memberSelection.find(m => m.id == id);
    if (!model) return;
    this.selectedMembers.push(model);
    this.memberSelection.splice(
      this.memberSelection.findIndex(m => m.id == model.id),
      1
    );
    this.memberSelectionControl.setValue(null);
    this.form.markAsDirty();
  }

  removeAccess(index: number) {
    if (this.selectedTargets.length <= index) return;

    let model = this.selectedTargets[index];
    this.selectedTargets.splice(index, 1);
    this.accessSelection.push(model);
    this.form.markAsDirty();
  }

  addAccess() {
    let id = this.targetSelectionControl.value?.id;
    if (!id) return;
    let model = this.accessSelection.find(m => m.id == id);
    if (!model) return;
    this.selectedTargets.push(model);
    this.accessSelection.splice(
      this.accessSelection.findIndex(m => m.id == model.id),
      1
    );
    this.targetSelectionControl.setValue(null);
    this.form.markAsDirty();
  }

  protected getFormGroup(): FormGroup {
    const group = this._fb.group({
      name: this._fcf.createMandatory('LOCATION_ACCESS.GROUP_NAME', this.dataContext.name),
      configurationLevel: this._fcf.createMandatory('LOCATION_ACCESS.CONFIG_LEVEL', this.dataContext.configurationLevel),
    });
    return group;
  }

  protected prepareSave(): LocationAccessGroup {
    const model = super.prepareSave();
    model.accessRows = this.selectedTargets.map(t => t.id);
    model.members = this.selectedMembers.map(t => t.id);
    return model;
  }
}
